import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  FolderOpen, 
  Trash2,
  Copy,
  Download,
  Search,
  Loader2,
  FileText,
  Mail,
  Building,
  Handshake,
  Heart,
  Share2,
  Presentation,
  Calendar
} from 'lucide-react';

const TYPE_ICONS = {
  sponsorship_proposal: Building,
  email_campaign: Mail,
  partnership_proposal: Handshake,
  grant_writeup: FileText,
  donation_appeal: Heart,
  social_media_campaign: Share2,
  board_presentation: Presentation,
};

const TYPE_COLORS = {
  sponsorship_proposal: 'bg-blue-100 text-blue-700',
  email_campaign: 'bg-green-100 text-green-700',
  partnership_proposal: 'bg-purple-100 text-purple-700',
  grant_writeup: 'bg-orange-100 text-orange-700',
  donation_appeal: 'bg-red-100 text-red-700',
  social_media_campaign: 'bg-pink-100 text-pink-700',
  board_presentation: 'bg-indigo-100 text-indigo-700',
};

const TYPE_NAMES = {
  sponsorship_proposal: 'Sponsorship Proposal',
  email_campaign: 'Email Campaign',
  partnership_proposal: 'Partnership Proposal',
  grant_writeup: 'Grant Write-up',
  donation_appeal: 'Donation Appeal',
  social_media_campaign: 'Social Media',
  board_presentation: 'Board Presentation',
};

export default function SavedTemplates() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchTemplates();
  }, [user, navigate]);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved-templates`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved-templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Template deleted');
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard!');
  };

  const downloadTemplate = (template) => {
    const blob = new Blob([template.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.name.replace(/\s+/g, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Downloaded!');
  };

  const filteredTemplates = templates.filter(t => 
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.generation_type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-alabaster" data-testid="saved-templates-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold text-primary-900 mb-2">
              My Templates
            </h1>
            <p className="text-stone-600">
              Your saved AI-generated content library
            </p>
          </div>
          
          <Button 
            onClick={() => navigate('/ai-generator')}
            className="bg-primary hover:bg-primary-800 text-white"
          >
            Create New
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white"
          />
        </div>

        {/* Templates Grid */}
        {filteredTemplates.length === 0 ? (
          <Card className="bg-white border-stone-200">
            <CardContent className="py-16 text-center">
              <FolderOpen className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-stone-700 mb-2">
                {searchQuery ? 'No templates found' : 'No saved templates yet'}
              </h3>
              <p className="text-stone-500 mb-6">
                {searchQuery 
                  ? 'Try a different search term' 
                  : 'Generate content and save it to your library for easy reuse'}
              </p>
              {!searchQuery && (
                <Button 
                  onClick={() => navigate('/ai-generator')}
                  className="bg-primary hover:bg-primary-800 text-white"
                >
                  Create Your First Template
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredTemplates.map((template) => {
              const TypeIcon = TYPE_ICONS[template.generation_type] || FileText;
              const colorClass = TYPE_COLORS[template.generation_type] || 'bg-stone-100 text-stone-700';
              const typeName = TYPE_NAMES[template.generation_type] || template.generation_type;
              
              return (
                <Card 
                  key={template.id} 
                  className="bg-white border-stone-200 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    setSelectedTemplate(template);
                    setIsViewDialogOpen(true);
                  }}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className={`w-10 h-10 ${colorClass} rounded-lg flex items-center justify-center`}>
                        <TypeIcon className="w-5 h-5" />
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {typeName}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg mt-3">{template.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-stone-500 line-clamp-3 mb-3">
                      {template.content.substring(0, 150)}...
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-stone-400 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(template.created_at).toLocaleDateString()}
                      </span>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(template.content);
                          }}
                          className="text-stone-400 hover:text-primary"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTemplate(template.id);
                          }}
                          className="text-stone-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* View Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>{selectedTemplate?.name}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto mt-4 bg-stone-50 rounded-lg p-6">
              <pre className="whitespace-pre-wrap text-sm text-stone-800 font-sans leading-relaxed">
                {selectedTemplate?.content}
              </pre>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => selectedTemplate && copyToClipboard(selectedTemplate.content)}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
              <Button 
                variant="outline" 
                onClick={() => selectedTemplate && downloadTemplate(selectedTemplate)}
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              <Button onClick={() => setIsViewDialogOpen(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
